'use client';

import { FC, useState } from 'react';

import { ContentfulOrderProduct, ContentfulProduct } from '@ravago/shared/page-data/models/contentful';
import { DisplayOrder } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';

import { getLanguageCode } from '../../../utils/locale/locale.utils';
import OrderLineFlyout from '../order-line-flyout/order-line-flyout';

import { DesktopShipmentTable, ShipmentTableProps } from './desktop-shipment-table';
import { MobileShipmentTable } from './mobile-shipment-table';

export const ShipmentTable: FC<ShipmentTableProps> = ({
   shipments,
   locale,
   isConsumer,
   acceptWebp,
   openedFrom,
   isUSBusinessContext,
   requestCoa,
   requestDeliveryNote,
   orderBaseUrls,
   requestBol,
   downloadDocument
}) => {
   const language = getLanguageCode(locale);
   const [flyoutOpen, setFlyoutOpen] = useState(false);
   const [orderId, setOrderId] = useState<number | null>(null);
   const [orderNumber, setOrderNumber] = useState<number | null>(null);
   const [contentfulProduct, setContentfulProduct] = useState<ContentfulOrderProduct | ContentfulProduct | undefined>(undefined);

   const onOrderLineButtonClick = async (orderLine: DisplayOrder) => {
      if (isConsumer) {
         const [id, orderNr] = orderLine.orderNumber.split('-').map(Number);
         setOrderId(id);
         setOrderNumber(orderNr);
         setContentfulProduct(orderLine.contentfulProduct);
         setFlyoutOpen(true);
      }
   };

   return (
      <>
         <Box margin={{ top: 'md' }} direction="column" gap="sm" width="100%">
            {shipments.map((shipment) => (
               <BreakpointSwitch key={shipment.shipmentId} includeViews={['desktop']}>
                  <DesktopShipmentTable
                     shipment={shipment}
                     language={language}
                     locale={locale}
                     acceptWebp={acceptWebp}
                     orderBaseUrls={orderBaseUrls}
                     onOrderLineButtonClick={onOrderLineButtonClick}
                     openedFrom={openedFrom}
                  />
               </BreakpointSwitch>
            ))}
            {shipments.map((shipment) => (
               <BreakpointSwitch key={shipment.shipmentId} includeViews={['phone', 'tab-port', 'tab-land']}>
                  <MobileShipmentTable
                     shipment={shipment}
                     language={language}
                     locale={locale}
                     acceptWebp={acceptWebp}
                     orderBaseUrls={orderBaseUrls}
                     openedFrom={openedFrom}
                     onOrderLineButtonClick={onOrderLineButtonClick}
                  />
               </BreakpointSwitch>
            ))}
         </Box>
         {orderId && orderNumber && (
            <OrderLineFlyout
               locale={locale}
               isUSBusinessContext={isUSBusinessContext}
               orderId={orderId}
               orderNumber={orderNumber}
               contentfulProduct={contentfulProduct}
               openedFrom={openedFrom}
               open={flyoutOpen}
               orderBaseUrls={orderBaseUrls}
               onFlyoutClose={() => setFlyoutOpen(false)}
               requestCoa={requestCoa}
               requestBol={requestBol}
               requestDeliveryNote={requestDeliveryNote}
               downloadDocument={downloadDocument}
            />
         )}
      </>
   );
};
